import React, { useState, useEffect } from 'react'
import { IoBarChart } from 'react-icons/io5'
import type { BuilderAngle } from '../types'
import { useBetBuilder } from '../context/BetContext'
import { useOdds } from '../context/OddsContext'
import { angleMarketToOddsMarket } from '../utils'
import { calculateValueDisplay } from '../utils/odds'
import { EdgeBadge } from './EdgeBadge'
import { FreshnessIndicator } from './FreshnessIndicator'
import { OddsComparisonModal } from './OddsComparisonModal'

interface MatchOddsMarketCardProps {
  marketType: string // e.g., 'match-winner', 'double-chance', 'draw-no-bet'
  marketLabel: string // Display label
  angles: BuilderAngle[] // All outcomes for this market
  fixtureId: number
  fixtureLabel: string
}

function getConfidenceStyle(confidence: string): React.CSSProperties {
  switch (confidence) {
    case 'high':
      return {
        background: 'rgba(74, 222, 128, 0.15)',
        borderColor: 'rgba(74, 222, 128, 0.4)',
        color: 'var(--green)',
      }
    case 'med':
      return {
        background: 'rgba(251, 191, 36, 0.15)',
        borderColor: 'rgba(251, 191, 36, 0.4)',
        color: 'var(--yellow)',
      }
    case 'low':
    default:
      return {
        background: 'rgba(251, 113, 133, 0.15)',
        borderColor: 'rgba(251, 113, 133, 0.4)',
        color: 'var(--red)',
      }
  }
}

function getConfidenceFromPct(pct: number) {
  if (pct >= 65) return 'high'
  if (pct >= 40) return 'med'
  return 'low'
}

// Individual outcome row component
function OutcomeRow({
  angle,
  fixtureId,
  fixtureLabel,
  oddsMarketSlug,
}: {
  angle: BuilderAngle
  fixtureId: number
  fixtureLabel: string
  oddsMarketSlug: string | null
}) {
  const [showTooltip, setShowTooltip] = useState(false)
  const [showCompare, setShowCompare] = useState(false)
  const { addBet, removeBet, isBetSaved } = useBetBuilder()
  const {
    getOddsForBookmaker,
    getBestOddsForSelection,
    preferredBookmaker,
    formatOdds,
    oddsHidden,
    showValueBadges,
    minEdgeThreshold,
  } = useOdds()

  const pct = angle.conviction_pct ?? 0
  const confidence = getConfidenceFromPct(pct)
  const confidenceStyle = getConfidenceStyle(confidence)

  // Get odds for this outcome
  const outcome = angle.entity?.outcome as string
  const preferredOdds = preferredBookmaker && oddsMarketSlug
    ? getOddsForBookmaker(fixtureId, oddsMarketSlug, outcome, preferredBookmaker.slug, null)
    : null
  const bestInfo = oddsMarketSlug
    ? getBestOddsForSelection(fixtureId, oddsMarketSlug, outcome, null)
    : null

  // Calculate edge values for value betting display
  const preferredEdge = showValueBadges && preferredOdds != null
    ? calculateValueDisplay(pct, preferredOdds, minEdgeThreshold)
    : null
  const bestEdge = showValueBadges && bestInfo?.odds != null
    ? calculateValueDisplay(pct, bestInfo.odds, minEdgeThreshold)
    : null

  // Build bet ID
  const betId = `${angle.market_id}-${outcome}`
  const isAdded = isBetSaved(betId)

  const handleAddBet = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isAdded) {
      removeBet(betId)
    } else {
      addBet({
        id: betId,
        fixtureId,
        fixture: fixtureLabel,
        title: angle.selection_label,
        line: undefined,
        marketType: 'other',
        scope: 'match',
        teamType: 'total',
        isOver: true,
        confidence,
        confidencePct: pct,
      })
    }
  }

  return (
    <div className="match-odds-row">
      <div className="match-odds-selection">
        <span className="match-odds-label">{angle.selection_label}</span>
      </div>
      <div className="match-odds-data">
        <div style={{ position: 'relative' }}>
          <span
            className="pill"
            style={{
              ...confidenceStyle,
              border: `1px solid ${confidenceStyle.borderColor}`,
              fontWeight: 700,
              fontSize: 12,
              padding: '3px 8px',
              cursor: 'help',
              minWidth: 42,
              textAlign: 'center',
            }}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            {pct}%
          </span>
          {showTooltip && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: 8,
                padding: '10px 12px',
                background: '#1a2340',
                border: '1px solid var(--border)',
                borderRadius: 8,
                zIndex: 100,
                minWidth: 160,
                boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
              }}
            >
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>
                {angle.why}
              </div>
            </div>
          )}
        </div>
        {!oddsHidden && (
          <>
            <span className="match-odds-value preferred">
              {preferredOdds != null ? formatOdds(preferredOdds) : '—'}
              {preferredEdge && (
                <EdgeBadge
                  value={preferredEdge}
                  ourProbability={pct}
                  bookmakerName={preferredBookmaker?.name}
                />
              )}
            </span>
            <span className="match-odds-value best">
              {bestInfo?.odds != null ? formatOdds(bestInfo.odds) : '—'}
              {bestEdge && (
                <EdgeBadge
                  value={bestEdge}
                  ourProbability={pct}
                  bookmakerName={bestInfo?.bookmaker_slug ? `Best: ${bestInfo.bookmaker_slug}` : 'Best odds'}
                />
              )}
            </span>
          </>
        )}
        {!oddsHidden && oddsMarketSlug && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              setShowCompare(true)
            }}
            className="compare-btn-small"
            title="Compare odds from all bookmakers"
          >
            <IoBarChart size={12} />
          </button>
        )}
        <button
          onClick={handleAddBet}
          className={`add-btn-small ${isAdded ? 'added' : ''}`}
        >
          {isAdded ? '✓' : '+'}
        </button>
      </div>

      {/* Odds Comparison Modal */}
      {oddsMarketSlug && (
        <OddsComparisonModal
          isOpen={showCompare}
          onClose={() => setShowCompare(false)}
          fixtureId={fixtureId}
          marketSlug={oddsMarketSlug}
          outcome={outcome}
          selectionLabel={angle.selection_label}
          ourProbability={pct}
        />
      )}
    </div>
  )
}

export function MatchOddsMarketCard({
  marketType,
  marketLabel,
  angles,
  fixtureId,
  fixtureLabel,
}: MatchOddsMarketCardProps) {
  const {
    fetchFixtureOddsData,
    forceRefreshOdds,
    getFixtureOddsData,
    preferredBookmaker,
    oddsHidden,
  } = useOdds()
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Fetch odds when component mounts
  useEffect(() => {
    if (fixtureId) {
      fetchFixtureOddsData(fixtureId)
    }
  }, [fixtureId, fetchFixtureOddsData])

  // Get current odds data to display freshness
  const oddsData = getFixtureOddsData(fixtureId)

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      await forceRefreshOdds(fixtureId)
    } finally {
      setIsRefreshing(false)
    }
  }

  // Map market type to odds market slug
  const oddsMarketSlug = angleMarketToOddsMarket(marketType, 'total')

  // Sort by conviction percentage (highest first)
  const sortedAngles = [...angles].sort((a, b) => (b.conviction_pct ?? 0) - (a.conviction_pct ?? 0))

  return (
    <div className="match-odds-table">
      {/* Table header */}
      <div className="match-odds-header">
        <div className="match-odds-selection">
          <span>Selection</span>
          {!oddsHidden && oddsData && (
            <FreshnessIndicator
              updatedAt={oddsData.updated_at}
              onRefresh={handleRefresh}
              showRefreshButton
              isRefreshing={isRefreshing}
            />
          )}
        </div>
        <div className="match-odds-data">
          <span>Prob</span>
          {!oddsHidden && (
            <>
              <span>{preferredBookmaker?.slug ?? 'Pref'}</span>
              <span>Best</span>
            </>
          )}
          <span></span>
        </div>
      </div>
      {/* Outcome rows */}
      {sortedAngles.map((angle, idx) => (
        <OutcomeRow
          key={idx}
          angle={angle}
          fixtureId={fixtureId}
          fixtureLabel={fixtureLabel}
          oddsMarketSlug={oddsMarketSlug}
        />
      ))}
    </div>
  )
}
